import { supabase } from '../config/supabase';

const DEV = import.meta.env.DEV;

/* ─── Permission Level Constants ─── */

export const PERMISSIONS = {
  FULL_ACCESS: 'full_access',
  SERVICE_ACCESS: 'service_access',
  LOG_ACCESS: 'log_access',
  VIEW_ONLY: 'view_only',
};

/** Higher number = more access */
const PERMISSION_RANK = {
  [PERMISSIONS.VIEW_ONLY]: 1,
  [PERMISSIONS.LOG_ACCESS]: 2,
  [PERMISSIONS.SERVICE_ACCESS]: 3,
  [PERMISSIONS.FULL_ACCESS]: 4,
};

/* ─── Internal Helper ─── */

/**
 * Fetch a single garage row from Supabase with permission-relevant columns.
 * @param {string} garageId
 * @returns {Promise<object|null>}
 */
async function fetchGarage(garageId) {
  if (!garageId) return null;

  try {
    // Use RPC to bypass RLS — invitees can't read garages they're not yet members of
    const { data, error } = await supabase.rpc('get_garage_for_collab', { garage_uuid: garageId });

    if (error) {
      console.error('[Permission] Fetch garage failed:', error.message);
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (DEV) console.log('[Permission] Fetched garage:', row?.id);
    return row || null;
  } catch (err) {
    console.error('[Permission] Fetch garage error:', err.message);
    return null;
  }
}

/* ─── Core Permission Functions ─── */

/**
 * Determine a user's permission level for a garage.
 *
 * @param {string} garageId - Supabase garage ID
 * @param {string} userId - Supabase auth user ID
 * @param {string} [userEmail] - Optional email for legacy collaborator fallback
 * @returns {Promise<string|null>} Permission level string or null if not a member
 */
export async function getUserPermission(garageId, userId, userEmail = null) {
  if (!garageId || !userId) return null;

  const garage = await fetchGarage(garageId);
  if (!garage) return null;

  // Owner always gets full access
  if (garage.owner_id === userId) {
    return PERMISSIONS.FULL_ACCESS;
  }

  // Check collaborator_permissions JSONB (keyed by userId)
  const perms = garage.collaborator_permissions || {};
  if (perms[userId]) {
    const level = perms[userId].permission;
    // Validate it's a known permission; default to VIEW_ONLY if unknown
    return PERMISSION_RANK[level] ? level : PERMISSIONS.VIEW_ONLY;
  }

  // Fallback: check legacy collaborators array by email
  if (userEmail) {
    const collabs = garage.collaborators || [];
    const found = collabs.some(
      (c) => c.email?.toLowerCase() === userEmail.toLowerCase()
    );
    if (found) {
      if (DEV) console.log('[Permission] Legacy collaborator found, defaulting to VIEW_ONLY');
      return PERMISSIONS.VIEW_ONLY;
    }
  }

  return null; // Not a member
}

/**
 * Check if a user has at least the required permission level.
 *
 * @param {string} garageId
 * @param {string} userId
 * @param {string} requiredPermission - One of PERMISSIONS values
 * @param {string} [userEmail]
 * @returns {Promise<boolean>}
 */
export async function hasPermission(garageId, userId, requiredPermission, userEmail = null) {
  const userPerm = await getUserPermission(garageId, userId, userEmail);
  if (!userPerm) return false;

  const userRank = PERMISSION_RANK[userPerm] || 0;
  const requiredRank = PERMISSION_RANK[requiredPermission] || 0;
  return userRank >= requiredRank;
}

/**
 * Get a garage's owner and collaborator permissions map.
 * Used by UI to render the team members list.
 *
 * @param {string} garageId
 * @returns {Promise<{owner_id: string|null, collaborator_permissions: object}>}
 */
export async function getGaragePermissions(garageId) {
  const garage = await fetchGarage(garageId);
  if (!garage) return { owner_id: null, collaborator_permissions: {} };
  return {
    owner_id: garage.owner_id,
    collaborator_permissions: garage.collaborator_permissions || {},
  };
}

/* ─── Convenience Helpers ─── */

/**
 * Can the user view vehicles in this garage?
 * Requires: VIEW_ONLY or higher
 */
export async function canViewVehicle(garageId, userId, userEmail) {
  return hasPermission(garageId, userId, PERMISSIONS.VIEW_ONLY, userEmail);
}

/**
 * Can the user log service entries and mileage?
 * Requires: LOG_ACCESS or higher
 */
export async function canLogService(garageId, userId, userEmail) {
  return hasPermission(garageId, userId, PERMISSIONS.LOG_ACCESS, userEmail);
}

/**
 * Can the user run AI diagnostics?
 * Requires: SERVICE_ACCESS or higher
 */
export async function canDiagnose(garageId, userId, userEmail) {
  return hasPermission(garageId, userId, PERMISSIONS.SERVICE_ACCESS, userEmail);
}

/**
 * Can the user edit vehicle details (mileage, trim, etc.)?
 * Requires: SERVICE_ACCESS or higher
 */
export async function canEditVehicle(garageId, userId, userEmail) {
  return hasPermission(garageId, userId, PERMISSIONS.SERVICE_ACCESS, userEmail);
}

/**
 * Can the user delete vehicles? Destructive action.
 * Requires: FULL_ACCESS
 */
export async function canDeleteVehicle(garageId, userId, userEmail) {
  return hasPermission(garageId, userId, PERMISSIONS.FULL_ACCESS, userEmail);
}

/**
 * Can the user invite or manage collaborators?
 * Requires: FULL_ACCESS
 */
export async function canInviteUsers(garageId, userId, userEmail) {
  return hasPermission(garageId, userId, PERMISSIONS.FULL_ACCESS, userEmail);
}

/* ─── Mutating Functions ─── */

/**
 * Add a user directly to a garage's collaborators and permissions.
 * Used by the invitation acceptance flow.
 *
 * @param {string} garageId
 * @param {string} userId - User's Supabase auth ID
 * @param {string} email - User's email
 * @param {string} permission - Permission level to grant
 * @param {string} grantedBy - User ID of who granted access
 * @returns {Promise<{data?: object, error?: string, code?: string}>}
 */
export async function addCollaboratorToGarage(garageId, userId, email, permission, grantedBy) {
  if (!garageId || !userId || !email) {
    return { error: 'Missing required parameters', code: 'MISSING_PARAMS' };
  }

  try {
    const garage = await fetchGarage(garageId);
    if (!garage) {
      return { error: 'Garage not found', code: 'NOT_FOUND' };
    }

    const normalizedEmail = email.trim().toLowerCase();
    const collabs = garage.collaborators || [];

    // Check for duplicate
    if (collabs.some((c) => c.email?.toLowerCase() === normalizedEmail)) {
      return { error: 'User is already a collaborator', code: 'DUPLICATE' };
    }

    // Use RPC to bypass RLS — invitee can't update a garage they don't own
    const { error } = await supabase.rpc('add_garage_collaborator', {
      garage_uuid: garageId,
      collab_user_id: userId,
      collab_email: normalizedEmail,
      collab_permission: permission,
      granted_by: grantedBy || null,
    });

    if (error) {
      console.error('[Permission] Add collaborator failed:', error.message);
      return { error: error.message, code: 'DB_ERROR' };
    }

    if (DEV) console.log('[Permission] Added collaborator:', normalizedEmail, 'as', permission);
    return { data: { id: garageId }, error: null };
  } catch (err) {
    console.error('[Permission] Add collaborator error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}

/**
 * Invite a collaborator to a garage — creates a pending invitation.
 * Only the garage owner can invite.
 *
 * @param {string} garageId
 * @param {string} inviterUserId - Must be the garage owner
 * @param {string} email - Email of the user to invite
 * @param {string} [permission='view_only'] - Permission level to grant
 * @param {string} [message] - Optional personal message
 * @returns {Promise<{data?: object, invitationCreated?: boolean, error?: string, code?: string}>}
 */
export async function inviteCollaborator(garageId, inviterUserId, email, permission = PERMISSIONS.VIEW_ONLY, message = null) {
  if (!garageId || !inviterUserId || !email) {
    return { error: 'Missing required parameters', code: 'MISSING_PARAMS' };
  }

  if (!PERMISSION_RANK[permission]) {
    return { error: `Invalid permission level: ${permission}`, code: 'INVALID_PERMISSION' };
  }

  try {
    const garage = await fetchGarage(garageId);
    if (!garage) {
      return { error: 'Garage not found', code: 'NOT_FOUND' };
    }

    // Only the garage owner can invite collaborators
    if (garage.owner_id !== inviterUserId) {
      return { error: 'Only the garage owner can invite collaborators', code: 'PERMISSION_DENIED' };
    }

    const normalizedEmail = email.trim().toLowerCase();

    // Check if already a collaborator
    const collabs = garage.collaborators || [];
    if (collabs.some((c) => c.email?.toLowerCase() === normalizedEmail)) {
      return { error: 'User is already a collaborator', code: 'DUPLICATE' };
    }

    // Create pending invitation instead of directly adding
    const { createInvitation } = await import('./invitationService');
    const result = await createInvitation(garageId, normalizedEmail, inviterUserId, permission, message);

    if (result.error) {
      return { error: result.error, code: result.code || 'INVITE_FAILED' };
    }

    if (DEV) console.log('[Permission] Created pending invitation for:', normalizedEmail);
    return { data: result.data, invitationCreated: true, error: null };
  } catch (err) {
    console.error('[Permission] Invite error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}

/**
 * Update a collaborator's permission level.
 * Only the garage owner can update permissions.
 *
 * @param {string} garageId
 * @param {string} updaterUserId - Must be the garage owner
 * @param {string} collaboratorId - Key in collaborator_permissions (UUID or pending:email)
 * @param {string} newPermission - New permission level
 * @returns {Promise<{data?: object, error?: string, code?: string}>}
 */
export async function updateCollaboratorPermission(garageId, updaterUserId, collaboratorId, newPermission) {
  if (!garageId || !updaterUserId || !collaboratorId || !newPermission) {
    return { error: 'Missing required parameters', code: 'MISSING_PARAMS' };
  }

  if (!PERMISSION_RANK[newPermission]) {
    return { error: `Invalid permission level: ${newPermission}`, code: 'INVALID_PERMISSION' };
  }

  try {
    const garage = await fetchGarage(garageId);
    if (!garage) {
      return { error: 'Garage not found', code: 'NOT_FOUND' };
    }

    // Only the garage owner can update permissions
    if (garage.owner_id !== updaterUserId) {
      return { error: 'Only the garage owner can update permissions', code: 'PERMISSION_DENIED' };
    }

    const perms = garage.collaborator_permissions || {};
    if (!perms[collaboratorId]) {
      return { error: 'Collaborator not found in permissions', code: 'NOT_FOUND' };
    }

    // Update the permission entry
    const updatedPermissions = {
      ...perms,
      [collaboratorId]: {
        ...perms[collaboratorId],
        permission: newPermission,
        updatedAt: new Date().toISOString(),
      },
    };

    const { data, error } = await supabase
      .from('garages')
      .update({ collaborator_permissions: updatedPermissions })
      .eq('id', garageId)
      .select('id, collaborator_permissions')
      .single();

    if (error) {
      console.error('[Permission] Update permission failed:', error.message);
      return { error: error.message, code: 'DB_ERROR' };
    }

    if (DEV) console.log('[Permission] Updated permission for', collaboratorId, 'to', newPermission);
    return { data, error: null };
  } catch (err) {
    console.error('[Permission] Update permission error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}

/**
 * Remove a collaborator from a garage.
 * Only the garage owner can remove collaborators.
 *
 * @param {string} garageId
 * @param {string} removerUserId - Must be the garage owner
 * @param {string} collaboratorId - Key in collaborator_permissions (UUID or pending:email)
 * @returns {Promise<{data?: object, error?: string, code?: string}>}
 */
export async function removeCollaborator(garageId, removerUserId, collaboratorId) {
  if (!garageId || !removerUserId || !collaboratorId) {
    return { error: 'Missing required parameters', code: 'MISSING_PARAMS' };
  }

  try {
    const garage = await fetchGarage(garageId);
    if (!garage) {
      return { error: 'Garage not found', code: 'NOT_FOUND' };
    }

    // Only the garage owner can remove collaborators
    if (garage.owner_id !== removerUserId) {
      return { error: 'Only the garage owner can remove collaborators', code: 'PERMISSION_DENIED' };
    }

    const perms = garage.collaborator_permissions || {};
    const entry = perms[collaboratorId];
    if (!entry) {
      return { error: 'Collaborator not found', code: 'NOT_FOUND' };
    }

    const emailToRemove = entry.email || null;

    // Use RPC to bypass RLS and handle JSONB manipulation server-side
    const { error } = await supabase.rpc('remove_garage_collaborator', {
      garage_uuid: garageId,
      collab_user_id: collaboratorId,
      collab_email: emailToRemove,
    });

    if (error) {
      console.error('[Permission] Remove failed:', error.message);
      return { error: error.message, code: 'DB_ERROR' };
    }

    if (DEV) console.log('[Permission] Removed collaborator:', collaboratorId);
    return { data: { id: garageId }, error: null };
  } catch (err) {
    console.error('[Permission] Remove error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}

/**
 * Leave a garage — for non-owners to remove themselves.
 *
 * @param {string} garageId
 * @param {string} userId - User leaving the garage
 * @param {string} userEmail - User's email
 * @returns {Promise<{data?: object, error?: string, code?: string}>}
 */
export async function leaveGarage(garageId, userId, userEmail) {
  if (!garageId || !userId) {
    return { error: 'Missing required parameters', code: 'MISSING_PARAMS' };
  }

  try {
    const garage = await fetchGarage(garageId);
    if (!garage) {
      return { error: 'Garage not found', code: 'NOT_FOUND' };
    }

    // Owner cannot leave their own garage
    if (garage.owner_id === userId) {
      return { error: 'Owner cannot leave their own garage', code: 'IS_OWNER' };
    }

    // Use RPC to bypass RLS — collaborators can't directly update the garages table
    const { error } = await supabase.rpc('remove_garage_collaborator', {
      garage_uuid: garageId,
      collab_user_id: userId,
      collab_email: userEmail || null,
    });

    if (error) {
      console.error('[Permission] Leave failed:', error.message);
      return { error: error.message, code: 'DB_ERROR' };
    }

    if (DEV) console.log('[Permission] Left garage:', garageId);
    return { data: { id: garageId }, error: null };
  } catch (err) {
    console.error('[Permission] Leave error:', err.message);
    return { error: err.message, code: 'UNEXPECTED' };
  }
}
